<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { NButton, NDropdown } from 'naive-ui'
import { useIsMobile } from '../composables/useIsMobile'
import { useHeaderMenu, type DropdownMixedOption } from '../composables/useHeaderMenu'
import { useSessionStore } from '../stores/session'
import { useSourceActivityStore } from '../stores/source_activity'
import { useLintNotesStore } from '../stores/lint_notes'
import { useSynthesisSuggestionsStore } from '../stores/synthesis_suggestions'
import { useWikiPagesStore } from '../stores/wiki_pages'
import { useAgentStore } from '../stores/agent'
import { useAgentHealthStore } from '../stores/agent_health'
import WikiHeaderMobileMenu from './WikiHeaderMobileMenu.vue'

// Only mounted once WikiHeader's own `v-if="currentUser"` confirms a
// signed-in user — this component re-reads the session store itself
// rather than taking `currentUser` as a prop, since every other piece
// of its state already comes from a global store the same way.

const emit = defineEmits<{
  (e: 'add-to-wiki'): void
  (e: 'threads'): void
}>()

const router = useRouter()
const isMobile = useIsMobile()

const session = useSessionStore()
const wikiPages = useWikiPagesStore()
const sourceActivity = useSourceActivityStore()
const lintNotes = useLintNotesStore()
const synthesisSuggestions = useSynthesisSuggestionsStore()
const agent = useAgentStore()
const agentHealth = useAgentHealthStore()

const { currentUser } = storeToRefs(session)
const { pages } = storeToRefs(wikiPages)
const { hasActive: addToWikiActive } = storeToRefs(sourceActivity)
const { openCount: lintOpenCount } = storeToRefs(lintNotes)
const { openCount: synthesisOpenCount } = storeToRefs(synthesisSuggestions)
const { sessions: agentSessions } = storeToRefs(agent)
const {
  pausedRepos,
  failedRepos,
  problemCount: agentProblemCount,
  unreachable: agentUnreachable,
} = storeToRefs(agentHealth)

// Combined badge count on the Admin button — admins want one signal
// for "things needing review", not a separate badge per queue. The
// per-queue counts still appear in the submenu labels ("Lint (3)" /
// "Synthesis (1)") so the breakdown is one click away.
const adminOpenCount = computed(() => lintOpenCount.value + synthesisOpenCount.value)

// Running agent sessions are a DIFFERENT category from the review backlog
// — an in-flight run, not something queued for human review. So it
// gets its own pulsing dot rather than folding into adminOpenCount. Kept
// live globally by useAdminSubscriptions.
const runningSessionCount = computed(() => agentSessions.value.filter((s) => s.running).length)

// Screen-reader label for the Admin trigger — the count badge + running dot
// are aria-hidden (decorative), so fold both signals into one spoken label.
const adminSignalLabel = computed(() => {
  const parts: string[] = []
  if (adminOpenCount.value > 0) parts.push(`${adminOpenCount.value} open for review`)
  if (runningSessionCount.value > 0) {
    parts.push(`${runningSessionCount.value} agent ${runningSessionCount.value === 1 ? 'session' : 'sessions'} running`)
  }
  if (pausedRepos.value.length > 0) {
    parts.push(`${pausedRepos.value.length} agent ${pausedRepos.value.length === 1 ? 'repo' : 'repos'} paused`)
  }
  if (failedRepos.value.length > 0) {
    parts.push(`${failedRepos.value.length} agent ${failedRepos.value.length === 1 ? 'repo' : 'repos'} failing`)
  }
  if (agentUnreachable.value) parts.push('agent status unreachable')
  return parts.length > 0 ? `Admin — ${parts.join(', ')}` : 'Admin'
})

// The mobile trigger folds the admin signal into its own label so AT users
// hear the backlog without opening the menu.
const mobileMenuLabel = computed(() =>
  isAdmin.value && adminSignalLabel.value !== 'Admin'
    ? `More actions — ${adminSignalLabel.value.slice('Admin — '.length)}`
    : 'More actions',
)

// Wiki Log: always a visible nav button; a red dot signals new entries
// since the user's last visit. The file is always at the wiki root.
const LOG_PATH = 'log.md'

const isAdmin = computed(() => currentUser.value?.admin === true)

const logPage = computed(() => pages.value.find((p) => p.path === LOG_PATH) ?? null)

// Defensive parse: a malformed ISO from either the page row or the
// user record gives NaN, and NaN > NaN is false — treat that as "no
// signal" so the dot doesn't flicker on a momentary bad value.
const logUnread = computed(() => {
  if (!logPage.value) return false
  const seenAt = currentUser.value?.last_seen_log_at
  if (!seenAt) return true
  const updated = new Date(logPage.value.updated_at).getTime()
  const seen = new Date(seenAt).getTime()
  if (Number.isNaN(updated) || Number.isNaN(seen)) return false
  return updated > seen
})

// Menu state (admin submenu, mobile hamburger, dispatcher) lives in
// `useHeaderMenu` so this view stays as layout. `Ask` and `Sign out`
// enter as injected callbacks because only this component has the wiring
// for them (parent-facing emit and the session store).
const { adminSubmenuItems, mobileMenu, renderMenuOption, onMenuSelect } = useHeaderMenu({
  isAdmin,
  onThreads: () => emit('threads'),
  onSignOut: () => {
    void signOut()
  },
})

// The single widening seam: useHeaderMenu keeps its menus typed
// as MenuItem[]/MenuGroup[] so `key: MenuKey` is enforced end-to-end;
// `<NDropdown :options>` wants the wider DropdownMixedOption union, and
// array invariance blocks the direct assignment, so bridge it here once
// per dropdown rather than erasing the key type back in the composable.
const adminDropdownOptions = computed(() => adminSubmenuItems.value as unknown as DropdownMixedOption[])
const mobileDropdownOptions = computed(() => mobileMenu.value as unknown as DropdownMixedOption[])

function goToLog() {
  // Fire-and-forget the seen call so navigation doesn't wait on the API.
  // The store patches currentUser locally on success, clearing the badge.
  session.markLogSeen()
  router.push(`/p/${LOG_PATH}`)
}

async function signOut() {
  await session.signOut()
}
</script>

<template>
  <div v-if="currentUser" class="flex items-center gap-2 min-w-0">
    <!-- Email hidden on mobile: it's too long to fit alongside the
         buttons on a 390px phone and is identity signal only, not an
         action. Desktop keeps the truncated span. -->
    <span class="hidden sm:inline text-sm text-white/40 truncate max-w-[140px]" :title="currentUser.email_address">{{
      currentUser.email_address
    }}</span>

    <NButton
      v-if="isAdmin"
      type="primary"
      size="small"
      data-testid="add-to-wiki"
      :title="addToWikiActive ? 'Add to brain — an ingestion is in flight' : 'Add to brain'"
      :aria-label="addToWikiActive ? 'Add to brain — an ingestion is in flight' : 'Add to brain'"
      @click="emit('add-to-wiki')"
    >
      <span class="relative inline-flex items-center">
        Add to brain
        <!-- The dot is purely visual signal — its status is announced
             by the button's own aria-label above. VoiceOver on Safari
             flattens nested aria-labels into the button's text, so
             surfacing the status as a sibling span's aria-label
             leaves AT users without it. -->
        <span
          v-if="addToWikiActive"
          data-testid="add-to-wiki-indicator"
          aria-hidden="true"
          class="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-status-alert"
        />
      </span>
    </NButton>

    <!-- Desktop-only buttons: on mobile these collapse into the
         hamburger dropdown below. Log stays visible on both. -->

    <NButton
      quaternary
      size="small"
      data-testid="log-button"
      :title="logUnread ? 'Brain Log — new entries since your last visit' : 'Brain Log'"
      :aria-label="logUnread ? 'Brain Log — new entries since your last visit' : 'Brain Log'"
      @click="goToLog"
    >
      <span class="relative inline-flex items-center">
        Log
        <!-- See add-to-wiki dot above: status announcement lives on
             the button's own aria-label so screen readers don't lose
             it when they flatten the nested span. -->
        <span
          v-if="logUnread"
          data-testid="log-unread-indicator"
          aria-hidden="true"
          class="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-status-alert"
        />
      </span>
    </NButton>

    <!-- Desktop Sessions button: agent sessions are
         frequently used, so they get a direct button instead of
         requiring Admin → System → Agent. Shows a pulsing dot when
         sessions are active. -->
    <NButton
      v-if="isAdmin && !isMobile"
      quaternary
      size="small"
      data-testid="sessions-button"
      :title="runningSessionCount > 0 ? `Agent sessions — ${runningSessionCount} running` : 'Agent sessions'"
      :aria-label="runningSessionCount > 0 ? `Agent sessions — ${runningSessionCount} running` : 'Agent sessions'"
      @click="router.push('/agent')"
    >
      <span class="relative inline-flex items-center">
        Sessions
        <span
          v-if="runningSessionCount > 0"
          data-testid="sessions-running-dot"
          aria-hidden="true"
          class="ml-1.5 w-2 h-2 rounded-full bg-brand-primary animate-pulse"
        />
      </span>
    </NButton>

    <!-- Desktop Admin dropdown — single entry point for New page,
         Lint, API tokens, and Jobs. Submenu contents come from the
         shared `adminSubmenuItems` so mobile hamburger and this
         dropdown never drift. The lint open count bubbles up to a
         badge on the trigger so admins see the backlog at a glance. -->
    <NDropdown
      v-if="isAdmin && !isMobile"
      trigger="click"
      :options="adminDropdownOptions"
      :render-label="renderMenuOption"
      @select="onMenuSelect"
    >
      <NButton
        quaternary
        size="small"
        data-testid="admin-menu"
        :title="adminSignalLabel"
        :aria-label="adminSignalLabel"
      >
        <span class="relative inline-flex items-center">
          Admin
          <span
            v-if="adminOpenCount > 0"
            data-testid="admin-open-badge"
            aria-hidden="true"
            class="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-status-alert text-[10px] font-semibold text-black"
            >{{ adminOpenCount }}</span
          >
          <span
            v-if="agentProblemCount > 0 || agentUnreachable"
            data-testid="admin-agent-health-badge"
            aria-hidden="true"
            class="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full border border-status-alert text-[10px] font-semibold text-status-alert"
            >{{ agentUnreachable && agentProblemCount === 0 ? '?' : `!${agentProblemCount}` }}</span
          >
          <span
            v-if="runningSessionCount > 0"
            data-testid="admin-running-dot"
            aria-hidden="true"
            class="ml-1.5 w-2 h-2 rounded-full bg-brand-primary animate-pulse"
          />
        </span>
      </NButton>
    </NDropdown>

    <!-- Sign out direct button is desktop-only. Mobile users always
         get a hamburger now (Ask is universal, so there's at least
         one collapsed item for every signed-in mobile user), and
         Sign out lives inside it to keep the header from overflowing
         on a 390px phone. -->
    <NButton v-if="!isMobile" quaternary size="small" @click="signOut">Sign out</NButton>

    <!-- Mobile hamburger: rendered for every signed-in mobile user since
         Ask is universal. Admin items layer on top of the base Ask + Sign
         out set in `mobileMenu`; the trigger carries the same combined
         review/running signal the desktop Admin button shows. -->
    <WikiHeaderMobileMenu
      v-if="isMobile"
      :options="mobileDropdownOptions"
      :render-label="renderMenuOption"
      :label="mobileMenuLabel"
      :is-admin="isAdmin"
      :admin-open-count="adminOpenCount"
      :running-session-count="runningSessionCount"
      @select="onMenuSelect"
    />
  </div>
</template>
